import { Link } from "gatsby";
import React, { useCallback, useContext, useEffect, useState } from "react";

import ButtonWrapper from "../../shared/components/button-wrapper/button-wrapper";
import SecondaryNav from "../../shared/components/secondary-nav/secondary-nav";
import ShIcon from "../../shared/components/sh-icon/sh-icon";
import ShLink from "../../shared/components/sh-link/sh-link";
import { UiStateContext } from "../../shared/contexts/ui-state/UiStateContext";
import useBrowserDetect from "../../shared/hooks/browser-detect/useBrowserDetect";
import useColumnEnd from "../../shared/hooks/column-end/useColumnEnd";
import useComponentAlerts from "../../shared/hooks/component-alerts/useComponentAlerts";
import useFeatureToggle from "../../shared/hooks/use-feature-toggle/useFeatureToggle";
import { FeatureToggle, IconPack, SecondaryNavContent, SubsiteContent } from "../../shared/models/app-data-model";
import "../shared/navigation-shared.scss";
import "./subsite-nav-mobile.scss";

export type SubsiteNavProps = {
  content: SubsiteContent;
  featureToggles: FeatureToggle[];
  iconPack?: IconPack[];
};

const SubsiteNavMobile: React.FC<SubsiteNavProps> = ({ content, featureToggles, iconPack }: SubsiteNavProps) => {
  const uiStateContext = useContext(UiStateContext);
  const isBrowser = useBrowserDetect();
  const subsiteNavTitleSwappingEnabled = useFeatureToggle("subsite-nav-title-vs-subsite-title", featureToggles);
  const [selectedSecondaryNavItem, setSelectedSecondaryNavItem] = useState([] as SecondaryNavContent[]);
  const [navColumns, setNavColumns] = useColumnEnd(selectedSecondaryNavItem);

  const primaryNav = content.subsite_nav?.find((el) => el.primary_nav)?.primary_nav;
  const openNavClass = uiStateContext.subsiteMenuOpen ? "expanded" : "";

  useComponentAlerts(content.alerts || [], "subsite");

  useEffect(() => {
    setNavColumns(selectedSecondaryNavItem);
  }, [selectedSecondaryNavItem, setNavColumns]);

  const toggleNavBody = (isRegional: boolean): void => {
    // when closing, remove the back button
    if (uiStateContext.subsiteMenuOpen) {
      // eslint-disable-next-line no-unused-expressions
      uiStateContext.setBackButtonNavItem?.(false);
    }

    if (isRegional) {
      uiStateContext.setSubsiteMenuOpen?.(true);
    } else {
      uiStateContext.setSubsiteMenuOpen?.(!uiStateContext.subsiteMenuOpen);
    }
  };

  //Opening of mega menu should collaps the subsite nav
  useEffect(() => {
    if (uiStateContext.megaMenuOpen) {
      uiStateContext.setSubsiteMenuOpen?.(false);
      uiStateContext.setBackButtonNavItem?.(false);
    }
  }, [uiStateContext.megaMenuOpen]);

  const handleBackButton = useCallback((): void => {
    uiStateContext.setBackButtonNavItem?.(false);
  }, [uiStateContext.backButtonNavItem]);

  // some funky logic below
  // usually if the subsite uses it's own subsite nav, we want to take that as the title
  // but if the subsite nav points to a different subsite nav, we want to use that as the title
  // so we'll use the nav title unless it has a `|` in it
  const subsiteNavTitle = content?.subsite_nav?.find((x) => x.title)?.title;
  const parentSubsiteLink = content.parent_subsite?.find((x) => x.url_prefix)?.url_prefix ?? null;
  const subsiteTitle = content?.subsite_name;
  const title = subsiteNavTitle?.includes("|") ? subsiteTitle : subsiteNavTitle;

  const primaryNavView = (
    <ul className="primary-nav">
      {primaryNav?.map((item, index) => {
        const navItemWithChildren = item.primary_nav_item?.reference?.find((el) => el.primary_nav);
        const navItemWithLink = item.primary_nav_item?.reference?.find((el) => el.url || el.link);
        const navItemWithRegionalLink = item.primary_nav_item?.reference?.find((el) => el.internal?.type === "sh_regional_link_component");

        if (navItemWithChildren) {
          return (
            <li key={index}>
              <span
                className="primary-nav-item"
                data-testid={`primary-nav-item-${index}`}
                onClick={() => {
                  // eslint-disable-next-line no-unused-expressions
                  uiStateContext.setBackButtonNavItem?.(true);
                  setSelectedSecondaryNavItem(navItemWithChildren.primary_nav as SecondaryNavContent[]);
                }}
              >
                {item.primary_nav_item.title}
                <ShIcon iconPack={iconPack} className="icon" iconName="caret_right" />
              </span>
            </li>
          );
        } else if (navItemWithLink || navItemWithRegionalLink) {
          return (
            <li key={index} onClick={() => toggleNavBody(navItemWithRegionalLink ? true : false)}>
              <ShLink
                title={item.primary_nav_item.title}
                className="primary-nav-item"
                reference={item.primary_nav_item.reference}
                regionalLinkAsButton={true}
                iconPack={iconPack}
              />
            </li>
          );
        }
      })}
    </ul>
  );

  const subsiteHeader = (
    <div className="subsite-nav-mobile-header" data-testid="subsite-nav-mobile-header-wrapper">
      <div className="subsite-nav-mobile-header-title" data-testid="subsite-nav-mobile-header">
        {uiStateContext.subsiteMenuOpen && parentSubsiteLink ? (
          <div className="subsite-nav-mobile-header-back-btn" data-testid="subsite-nav-mobile-header-back-btn">
            <Link to={parentSubsiteLink} className="subsite-nav-mobile-back-link" data-testid="subsite-nav-mobile-back-link">
              <ShIcon iconPack={iconPack} className="icon back-arrow-icon" iconName="arrow" />
              {content?.parent_subsite?.find((el) => el.subsite_name)?.subsite_name}
            </Link>
          </div>
        ) : null}
        <div className="subsite-nav-title" title={subsiteNavTitleSwappingEnabled ? title : subsiteTitle}>
          {subsiteNavTitleSwappingEnabled ? title : subsiteTitle}
        </div>
      </div>
      <div className="subsite-nav-toggle-icon" data-testid="subsite-nav-toggle-icon" onClick={() => toggleNavBody(false)}>
        {uiStateContext.subsiteMenuOpen ? (
          <button className="unstyled-btn subsite-nav-caret" data-testid="subsite-nav-caret">
            <ShIcon iconPack={iconPack} className="icon caret-up-icon" iconName="caret_up" />
          </button>
        ) : (
          <button className="unstyled-btn subsite-nav-caret" data-testid="subsite-nav-caret">
            <ShIcon iconPack={iconPack} className="icon caret-down-icon" iconName="caret_up" />
          </button>
        )}
      </div>
    </div>
  );

  const subsiteHeaderWithOpenPrimaryNav = (
    <div className="subsite-nav-mobile-header" data-testid="subsite-nav-mobile-header-wrapper">
      <div className="subsite-nav-mobile-header-title" data-testid="subsite-nav-mobile-header">
        {uiStateContext.subsiteMenuOpen ? (
          <div className="subsite-nav-mobile-header-back-btn" data-testid="subsite-nav-mobile-header-back-btn">
            <button className="unstyled-btn subsite-nav-back-btn" data-testid="subsite-nav-back-btn" onClick={() => handleBackButton()}>
              <ShIcon iconPack={iconPack} className="icon back-arrow-icon" iconName="arrow" />
            </button>
          </div>
        ) : null}
      </div>
      <div className="subsite-nav-toggle-icon" onClick={() => toggleNavBody(false)}>
        <button className="unstyled-btn subsite-nav-caret" data-testid="subsite-nav-caret">
          <ShIcon iconPack={iconPack} className="icon caret-up-icon" iconName="caret_up" />
        </button>
      </div>
    </div>
  );

  // todo: should probably use button?.reference?.length instead of !== []
  const button = content?.subsite_nav?.find((x) => x.button)?.button?.text &&
    content?.subsite_nav?.find((x) => x.button)?.button?.reference !== [] && (
      <span className={`subsite-nav-mobile-button ${isBrowser}`}>
        <ButtonWrapper
          type="gradient"
          label={content?.subsite_nav.find((x) => x.button)?.button?.text}
          reference={content?.subsite_nav?.find((x) => x.button)?.button?.reference}
          iconPack={iconPack}
        />
      </span>
    );

  return (
    <>
      {content?.subsite_nav?.length > 0 && (
        <div className={`subsite-nav-mobile ${openNavClass}`}>
          {uiStateContext.backButtonNavItem && selectedSecondaryNavItem ? (
            <div>{subsiteHeaderWithOpenPrimaryNav}</div>
          ) : (
            <div>{subsiteHeader}</div>
          )}
          {uiStateContext.subsiteMenuOpen && (
            <nav className="subsite-nav-mobile-body" data-testid="subsite-nav-mobile-body">
              {uiStateContext.backButtonNavItem && selectedSecondaryNavItem ? (
                <SecondaryNav columns={navColumns} isOutsideClickHandlerEnabled={true} iconPack={iconPack} />
              ) : (
                <div>
                  {primaryNavView}
                  {button}
                </div>
              )}
            </nav>
          )}
        </div>
      )}
    </>
  );
};

export default SubsiteNavMobile;
